import type { TickCtx, GameState } from '../tickTypes'
import { Dec } from '../../shared/decimal'
import { magifolds, magicHardcapValue } from '../formulas'

// updateSmall section: script.js:1447-1475 (platinum), :1476-1522 (magic),
// :1523-1573 (challenges/magifolds). State math only; DOM writes stay in
// pristine updateSmall.
export function platinumMagicChallenges(game: GameState, ctx: TickCtx): void {
  // getMagicGain (magic.js:211-232), inlined verbatim minus nothing — the
  // bare-global magicHardcap write is the promoted transient box and the cap
  // computation is the canonical formulas.magicHardcapValue.
  function getMagicGain(): any {
    let toGet: any = game.gold.div(1e15).pow(0.1) // magic.js:212
    if (game.magicUpgradesBought[3]) { // magic.js:213-216
      if (game.magicUpgradesBought[9]) { toGet = toGet.mul(game.magic.add(1).log2().mul(4).add(1)) }
      else { toGet = toGet.mul(game.magic.add(1).log2().mul(1.5).add(1)) }
    }
    if (game.magicUpgradesBought[1]) toGet = toGet.mul(1.5) // magic.js:217
    if (game.magicUpgradesBought[6]) toGet = toGet.mul(2)
    if (game.magicUpgradesBought[17]) toGet = toGet.mul(3)
    if (game.unlocks >= 10) toGet = toGet.mul(game.cyanSigils.add(1).pow(2)) // magic.js:221
    if (game.unlocks >= 11) toGet = toGet.mul(game.blueSigils.add(1).pow(3))
    if (game.unlocks >= 12) toGet = toGet.mul(game.indigoSigils.add(1).pow(4))
    if (game.unlocks >= 13) toGet = toGet.mul(game.violetSigils.add(1).pow(5))
    if (game.unlocks >= 14) toGet = toGet.mul(game.pinkSigils.add(1).pow(6))
    if (game.darkMagicUpgradesBought[9] == true) toGet = toGet.pow(1.1) // magic.js:226
    if (game.darkMagicUpgradesBought[1]) toGet = toGet.pow(game.uranium.add(1).log10().div(30).add(1))
    ctx.transients.magicHardcap.value = magicHardcapValue(game) // magic.js:228-229
    if (toGet.gt(ctx.transients.magicHardcap.value as any)) toGet = ctx.transients.magicHardcap.value // magic.js:230
    toGet = toGet.floor() // magic.js:231
    return toGet
  }

  // script.js:1447-1475 — platinumToGet / bestPlatinumToGet / the
  // platinumUpgrade6Effect transient.
  if (game.unlocks >= 3) {
    // script.js:1451-1465
    game.platinumToGet = game.gold.add(1).log2().mul(new Dec(3).pow(game.fireUpgrade6Bought.pow(0.6)))
    if (game.unlocks >= 7) game.platinumToGet = game.platinumToGet.mul(1.2 ** game.uraniumUpgradesBought[0])
    if (game.unlocks >= 8) game.platinumToGet = game.platinumToGet.mul(10 ** game.uraniumUpgradesBought[4])
    if (game.unlocks >= 11) game.platinumToGet = game.platinumToGet.mul(new Dec(1e15).pow(game.blueSigilUpgradesBought[1].pow(0.6)))
    if (game.holyTetrahedronUpgradesBought[4]) game.platinumToGet = game.platinumToGet.mul(1e20)
    if (game.unlocks >= 17) game.platinumToGet = game.platinumToGet.pow(game.blueFireUpgradesBought[2].add(1).log2().add(1))
    if (game.darkMagicUpgradesBought[12]) game.platinumToGet = game.platinumToGet.pow(666)
    if (game.challengesActive && game.selectedChallenges[1]) game.platinumToGet = game.platinumToGet.pow(0.25)
    if (game.inHell) { // script.js:1459-1464 hell-layer ladder
      if (game.currentHellLayer == 1) game.platinumToGet = game.platinumToGet.pow(0.1)
      else if (game.currentHellLayer == 2) game.platinumToGet = game.platinumToGet.pow(0.01)
      else if (game.currentHellLayer == 3 || game.currentHellLayer == 4) game.platinumToGet = game.platinumToGet.add(1).log10()
      else if (game.currentHellLayer == 5) game.platinumToGet = game.platinumToGet.add(1).log10().pow(0.5)
    }
    game.platinumToGet = game.platinumToGet.floor()
    // script.js:1467 high-water
    if (game.platinumToGet.gt(game.bestPlatinumToGet)) game.bestPlatinumToGet = game.platinumToGet
    // script.js:1469-1471 — bare global platinumUpgrade6Effect, promoted to
    // the transient box (incl. the challenge-4 div(1e25) line).
    const p6 = ctx.transients.platinumUpgrade6Effect
    if (game.uraniumUpgradesBought[3]) { p6.value = game.platinum.add(1).pow(game.platinumUpgradesBought[5] * 1.2) }
    else { p6.value = game.platinum.add(1).log10().add(1).pow(game.platinumUpgradesBought[5] * 1.2) }
    if (game.challengesActive && game.selectedChallenges[3]) p6.value = (p6.value as any).div(1e25)
  }

  // script.js:1476-1522 — magicToGet + magicEffect. (:1496 getNextMagicAt is
  // display-only; :1478-1492 are the author's commented-out old pipeline.)
  if (game.unlocks >= 4) {
    game.magicToGet = getMagicGain() // script.js:1493
    // script.js:1500-1510
    game.magicEffect = game.magic.mul(3).mul(new Dec(1.5).pow(game.platinumUpgradesBought[6])).add(1)
    if (game.magicUpgradesBought[15]) game.magicEffect = game.magicEffect.pow(2)
    if (game.plutoniumUpgradesBought[1] > 0) game.magicEffect = game.magicEffect.pow((game.plutoniumUpgradesBought[1] * 100000) ** 0.8 + 1)
    if (game.inHell && game.currentHellLayer >= 5) game.magicEffect = game.magicEffect.add(1).log10()
    if (game.challengesActive && game.selectedChallenges[0]) game.magicEffect = game.magic.pow(0.2).div(1e15)
    if (game.magicEffect.gt('e1000')) {
      game.magicEffect = game.magicEffect.mul('e1000').pow(0.5) // softcap
    }
    if (game.magicEffect.gt('e100000')) { game.magicEffect = game.magicEffect.mul('e900000').pow(0.1) }
  }

  // script.js:1523-1573 — magicScoreToGet, score high-waters, magifolds.
  if (game.unlocks >= 5) {
    // script.js:1525-1543 (:1527 reads LAST tick's magifolds — order sacred)
    game.magicScoreToGet = game.gold.add(1).multiply(game.fire.add(1)).log10()
    if (game.magicUpgradesBought[12]) game.magicScoreToGet = game.magicScoreToGet.mul(2)
    if (game.magicUpgradesBought[16]) game.magicScoreToGet = game.magicScoreToGet.mul(game.magifolds.log10().add(1).div(3))
    if (game.unlocks >= 7) game.magicScoreToGet = game.magicScoreToGet.mul(2 ** game.uraniumUpgradesBought[2])
    if (game.darkMagicUpgradesBought[6]) game.magicScoreToGet = game.magicScoreToGet.mul(2)
    if (game.unlocks >= 10) game.magicScoreToGet = game.magicScoreToGet.mul(game.cyanSigils.add(1).pow(2))
    if (game.unlocks >= 11) game.magicScoreToGet = game.magicScoreToGet.mul(game.blueSigils.add(1).pow(3))
    if (game.unlocks >= 13) game.magicScoreToGet = game.magicScoreToGet.mul(game.violetSigils.add(1).pow(5))
    if (game.unlocks >= 21) game.magicScoreToGet = game.magicScoreToGet.mul(new Dec(10).pow(game.redSigils.pow(0.5).mul(400)))
    if (game.unlocks >= 22) game.magicScoreToGet = game.magicScoreToGet.mul(new Dec(10).pow(game.orangeSigils.pow(0.5).mul(800)))
    if (game.unlocks >= 23) game.magicScoreToGet = game.magicScoreToGet.mul(new Dec(10).pow(game.yellowSigils.pow(0.5).mul(1600)))
    if (game.unlocks >= 14) { // script.js:1536-1539
      game.magicScoreToGet = game.magicScoreToGet.mul(game.pinkSigils.add(1).pow(6))
      game.magicScoreToGet = game.magicScoreToGet.pow(new Dec(1.5).pow(game.pinkSigilUpgradesBought[2].pow(0.5)))
    }
    if (game.darkMagicUpgradesBought[0]) game.magicScoreToGet = game.magicScoreToGet.pow(1.3)
    if (game.holyTetrahedronUpgradesBought[1]) game.magicScoreToGet = game.magicScoreToGet.pow(2)
    if (game.holyOctahedronUpgradesBought[5]) game.magicScoreToGet = game.magicScoreToGet.pow(100)
    game.magicScoreToGet = game.magicScoreToGet.floor()
    // script.js:1544-1565 — achievement 8 tier-2 gate
    if (game.unlockedAchievements[7] > 1) {
      game.magicScore1 = Dec.max(game.magicScoreToGet, game.magicScore1)
      game.magicScore2 = Dec.max(game.magicScoreToGet, game.magicScore2)
      game.magicScore3 = Dec.max(game.magicScoreToGet, game.magicScore3)
      game.magicScore4 = Dec.max(game.magicScoreToGet, game.magicScore4)
      // script.js:1557-1558, via the canonical formula (inHell -> 1, else
      // the four-score product) — redirected site.
      game.magifolds = magifolds(game)
    }
    // script.js:1566-1572 — outside challenges the score-to-get resets to 0.
    if (!game.challengesActive) {
      game.magicScoreToGet = new Dec(0)
    }
  }
}
